import "./OptionsChain.scss";
import {IModalInstanceService} from "angular-ui-bootstrap";
import {OptionChain} from "../../option/chain/OptionChain";
import {OptionExpiry} from "../../option/chain/OptionExpiry";
import {IOnDestroy, IOnInit, IScope, ITimeoutService} from "angular";
import {HistoricalOptionRecord} from "../../price/historical/HistoricalPriceModels";
import {PriceSource, PriceSummary} from "../../price/source/PriceSource";
import {Observable, ReplaySubject, Subject} from "rxjs";
import "rxjs/add/observable/combineLatest";
import {HistoricalOption} from "./models/HistoricalOption";
import {Optional} from "../../../js/util/Optional";
import {Expiry} from "../../expiry/ExpiryService";
import {formatDate} from "../../../js/util/DateTimeUtil";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {IsoDateString} from "../../../pseudo-types/DateTypes";
import moment = require("moment");

interface ExpiryChange {
    year: string;
    expiry?: string;
}

export class OptionsChainController implements IOnInit, IOnDestroy {

    public availableYears: Array<string>;
    public selectedYear: string;
    public availableExpiries: Array<Expiry>;
    public selectedExpiry: string;

    public underlyingPrice: PriceSummary;
    public strikes: Array<number>;
    public puts: Array<HistoricalOption>;
    public calls: Array<HistoricalOption>;

    private expiryChanged = new ReplaySubject<ExpiryChange>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $uibModalInstance: IModalInstanceService,
                       private chain: OptionChain,
                       private priceSource: PriceSource,
                       private $scope: IScope,
                       private $timeout: ITimeoutService,
                       private previousCode?: string) {
    }

    public $onInit(): void {
        Observable.combineLatest(this.expiryChanged, this.priceSource.updates())
                  .debounceTime(200)
                  .takeUntil(this.destroyed$)
                  .subscribe(([change, price]) => this.updateGridData(change, price));
        this.selectInitialDisplay();
    }

    private updateGridData(change: ExpiryChange, price: PriceSummary) {
        const {year, expiry} = change;
        this.selectedYear = year;
        this.availableExpiries = this.chain.expiriesForYear(year);
        this.selectedExpiry = Optional.of(expiry)
                                      .orElseGet(() => formatDate(this.availableExpiries[0].date, "YYYY-MM-DD"));
        const optionExpiry = this.chain.expiry(IsoDateString(this.selectedExpiry));

        optionExpiry.ifPresent(exp => this.updateFromExpiry(exp, price))
                    .ifEmpty(this.clearData.bind(this));

        this.$timeout(this.scrollToInTheMoney.bind(this), 200);
        this.$scope.$applyAsync();
    }

    private scrollToInTheMoney() {
        const itmPercentage = this.findInTheMoneyPoint();
        const el = $(".option-chain-modal .grid-body-container .scroll-container");
        const scrollTo = (el[0].scrollHeight * itmPercentage) - ((el.height()!) / 2);
        el.slimScroll({scrollTo: scrollTo.toFixed(0) + "px"});
    }

    private findInTheMoneyPoint(): number {
        if (this.puts.length === 0) {
            return 0;
        }
        const index = this.puts
                          .filter(put => !ObjectUtil.isEmpty(put.record))
                          .findIndex((item, idx, arr) => {
                              return Optional.of(arr)
                                             .map(a => a[idx + 1])
                                             .map(next => next.inTheMoney !== item.inTheMoney)
                                             .orElse(false);
                          });
        const size = this.puts.length;
        return (index + 1) / size;
    }

    private updateFromExpiry(expiry: OptionExpiry, summary: PriceSummary) {
        this.underlyingPrice = summary;
        this.strikes = expiry.strikes;
        this.puts = expiry.puts.map(p => this.buildPut(p, summary.price));
        this.calls = expiry.calls.map(c => this.buildCall(c, summary.price));
    }

    private buildPut(put: HistoricalOptionRecord, underlyingPrice: number): HistoricalOption {
        if (put == null) {
            return HistoricalOption.empty();
        }
        const inTheMoney = put.strike > underlyingPrice;
        const selected = put.code === this.previousCode;
        return HistoricalOption.create(put, inTheMoney, selected);
    }

    private buildCall(call: HistoricalOptionRecord, underlyingPrice: number): HistoricalOption {
        if (call == null) {
            return HistoricalOption.empty();
        }
        const inTheMoney = call.strike < underlyingPrice;
        const selected = call.code === this.previousCode;
        return HistoricalOption.create(call, inTheMoney, selected);
    }

    private clearData() {
        this.strikes = [];
        this.puts = [];
        this.calls = [];
    }

    private selectInitialDisplay() {
        this.availableYears = this.chain.years();
        if (this.previousCode == null) {
            this.selectDefaultStart();
        } else {
            const expiry = this.chain.expiryForCode(this.previousCode);
            expiry.ifPresent(this.selectFromExpiry.bind(this))
                  .ifEmpty(this.selectDefaultStart.bind(this));
        }
    }

    private selectDefaultStart() {
        const selectedYear = this.availableYears[0];
        this.expiryChanged.next({year: selectedYear});
    }

    private selectFromExpiry(expiry: string) {
        const m = moment(expiry);
        const year = m.format("YYYY");
        this.expiryChanged.next({year, expiry});
    }

    public selectYear(year: string) {
        if (year !== this.selectedYear) {
            this.expiryChanged.next({year});
        }
    }

    public selectExpiry(expiry: string) {
        if (expiry !== this.selectedExpiry) {
            this.expiryChanged.next({year: this.selectedYear, expiry});
        }
    }

    public selectOption(option: HistoricalOption) {
        this.$uibModalInstance.close(option.record);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
